import { format } from "date-fns"
import { AlertTriangle, ArrowLeft, Banknote, Briefcase, Calendar, Clock, Edit, Globe, MapPin, Tag, Users } from "lucide-react"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { toast } from "@/components/ui/use-toast"

import { Gender, LanguageProficiency, RequestPriority, RequestStatus, SalaryRange, TechStackLevel, WorkingModel } from "@/components/resource-requests/create/types"

// Mock data service function - would be replaced with actual API call
const fetchResourceRequestById = async (id: string) => {
  // Simulate API call
  await new Promise(resolve => setTimeout(resolve, 500))
  
  // Return mock data based on schema
  return {
    id,
    title: "Senior React Developer",
    department: "Engineering",
    position: "Developer",
    numberOfPositions: 2,
    description: "We are looking for an experienced React developer to join our growing engineering team. The ideal candidate will have a strong understanding of React, TypeScript, and modern frontend development practices.",
    requirements: [
      "5+ years of experience with React",
      "Strong TypeScript skills",
      "Experience with state management libraries",
      "Knowledge of performance optimization techniques"
    ],
    skills: [
      "React", "TypeScript", "Redux", "GraphQL", "REST API", "CSS-in-JS", "Jest", "React Testing Library"
    ],
    workingModels: [WorkingModel.HYBRID, WorkingModel.REMOTE],
    gender: Gender.ANY,
    techStacks: [
      { name: "React", level: TechStackLevel.SENIOR, yearsOfExperience: 5 },
      { name: "TypeScript", level: TechStackLevel.SENIOR, yearsOfExperience: 4 },
      { name: "Node.js", level: TechStackLevel.MID, yearsOfExperience: 3 }
    ],
    workingTime: "40 hours per week, flexible schedule",
    keywords: ["frontend", "react", "typescript", "senior"],
    languages: [
      { name: "English", proficiency: LanguageProficiency.FLUENT },
      { name: "Spanish", proficiency: LanguageProficiency.BASIC }
    ],
    experience: "5+ years of experience with React and modern JavaScript frameworks",
    education: "Bachelor's degree in Computer Science or related field",
    salary: {
      min: 4000,
      max: 6000,
      currency: "USD",
      range: SalaryRange.ABOVE_2000
    },
    status: RequestStatus.OPEN,
    priority: RequestPriority.HIGH,
    requestedBy: {
      id: "u1",
      name: "John Doe"
    },
    approvedBy: {
      id: "u2",
      name: "Jane Smith"
    },
    approvalDate: new Date("2023-01-10"),
    requestDate: new Date("2023-01-05"),
    targetStartDate: new Date("2023-02-15"),
    targetEndDate: new Date("2023-12-31"),
    requestDeadline: new Date("2023-01-31"),
    budget: 150000,
    notes: "This is a critical hire for our Q1 roadmap. We need someone who can hit the ground running and contribute to our product development immediately.",
    projectCode: "PRJ-2023-001",
    requiresCodeLiveTest: true
  }
}

export default function ResourceRequestDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [resourceRequest, setResourceRequest] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusDialogOpen, setStatusDialogOpen] = useState(false)
  const [newStatus, setNewStatus] = useState<RequestStatus | "">("")

  useEffect(() => {
    const loadResourceRequest = async () => {
      try {
        if (id) {
          setLoading(true)
          const data = await fetchResourceRequestById(id)
          setResourceRequest(data)
        }
      } catch (err) {
        setError("Failed to load resource request")
        console.error(err)
      } finally {
        setLoading(false)
      }
    }

    loadResourceRequest()
  }, [id])

  const handleStatusChange = async () => {
    try {
      if (newStatus) {
        // Simulate API call to update status
        await new Promise(resolve => setTimeout(resolve, 500))
        
        // Update local state
        setResourceRequest({
          ...resourceRequest,
          status: newStatus
        })
        
        toast({
          title: "Status updated",
          description: `Request status has been updated to ${newStatus}`,
        })
        
        setStatusDialogOpen(false)
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      })
    }
  }

  const getPriorityBadgeColor = (priority: RequestPriority) => {
    switch (priority) {
      case RequestPriority.URGENT:
        return "bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-900 dark:text-red-100"
      case RequestPriority.HIGH:
        return "bg-orange-100 text-orange-800 hover:bg-orange-100 dark:bg-orange-900 dark:text-orange-100"
      case RequestPriority.MEDIUM:
        return "bg-blue-100 text-blue-800 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-100"
      case RequestPriority.LOW:
        return "bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-100"
      default:
        return ""
    }
  }

  const getStatusBadgeColor = (status: RequestStatus) => {
    switch (status) {
      case RequestStatus.DRAFT:
        return "bg-gray-100 text-gray-800 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-100"
      case RequestStatus.OPEN:
        return "bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-100"
      case RequestStatus.PENDING:
        return "bg-yellow-100 text-yellow-800 hover:bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-100"
      case RequestStatus.CLOSED:
        return "bg-blue-100 text-blue-800 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-100"
      default:
        return ""
    }
  }

  if (loading) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    )
  }

  if (error || !resourceRequest) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex flex-col items-center justify-center h-64">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mb-4" />
          <h2 className="text-2xl font-bold">Error Loading Resource Request</h2>
          <p className="text-muted-foreground">{error || "Resource request not found"}</p>
          <Button 
            className="mt-4" 
            variant="outline" 
            onClick={() => navigate("/resource-requests")}
          >
            Back to Resource Requests
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header and Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navigate("/resource-requests")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{resourceRequest.title}</h1>
            <p className="text-muted-foreground">Resource Request ID: {resourceRequest.id}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                Change Status
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Change Request Status</DialogTitle>
                <DialogDescription>
                  Select a new status for this resource request.
                </DialogDescription>
              </DialogHeader>
              <Select value={newStatus} onValueChange={(value) => setNewStatus(value as RequestStatus)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={RequestStatus.DRAFT}>Draft</SelectItem>
                  <SelectItem value={RequestStatus.OPEN}>Open</SelectItem>
                  <SelectItem value={RequestStatus.PENDING}>Pending</SelectItem>
                  <SelectItem value={RequestStatus.CLOSED}>Closed</SelectItem>
                </SelectContent>
              </Select>
              <DialogFooter>
                <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleStatusChange}>Save Changes</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button variant="outline" onClick={() => navigate(`/resource-requests/${id}/edit`)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">Close Request</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will close the resource request. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    setResourceRequest({
                      ...resourceRequest,
                      status: RequestStatus.CLOSED
                    })
                    
                    toast({
                      title: "Request Closed",
                      description: "The resource request has been closed",
                    })
                  }}
                >
                  Close Request
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Status and Metadata */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge className={`${getStatusBadgeColor(resourceRequest.status)}`}>
              {resourceRequest.status}
            </Badge>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Priority</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge className={`${getPriorityBadgeColor(resourceRequest.priority)}`}>
              {resourceRequest.priority}
            </Badge>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Requested By</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-medium">
              {resourceRequest.requestedBy.name.charAt(0)}
            </div>
            <span>{resourceRequest.requestedBy.name}</span>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Request Date</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span>{format(new Date(resourceRequest.requestDate), "PPP")}</span>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-4 md:w-auto md:inline-flex">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="requirements">Requirements</TabsTrigger>
          <TabsTrigger value="working-conditions">Working Conditions</TabsTrigger>
          <TabsTrigger value="timelines">Timeline & Compensation</TabsTrigger>
        </TabsList>
        
        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-medium">Title</h3>
                  <p className="text-muted-foreground">{resourceRequest.title}</p>
                </div>
                <div>
                  <h3 className="font-medium">Department</h3>
                  <p className="text-muted-foreground">{resourceRequest.department}</p>
                </div>
                <div>
                  <h3 className="font-medium">Position</h3>
                  <p className="text-muted-foreground">{resourceRequest.position}</p>
                </div>
                <div>
                  <h3 className="font-medium">Number of Positions</h3>
                  <p className="text-muted-foreground">{resourceRequest.numberOfPositions}</p>
                </div>
                <div>
                  <h3 className="font-medium">Project Code</h3>
                  <p className="text-muted-foreground">{resourceRequest.projectCode || "N/A"}</p>
                </div>
              </CardContent>
            </Card>
            
            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-line">{resourceRequest.description}</p>
              </CardContent>
            </Card>
          </div>
          
          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Key Dates</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <h3 className="font-medium flex items-center gap-1">
                    <Calendar className="h-4 w-4" /> Request Date
                  </h3>
                  <p className="text-muted-foreground">
                    {resourceRequest.requestDate ? format(new Date(resourceRequest.requestDate), "PPP") : "N/A"}
                  </p>
                </div>
                <div>
                  <h3 className="font-medium flex items-center gap-1">
                    <Calendar className="h-4 w-4" /> Target Start Date
                  </h3>
                  <p className="text-muted-foreground">
                    {resourceRequest.targetStartDate ? format(new Date(resourceRequest.targetStartDate), "PPP") : "N/A"}
                  </p>
                </div>
                <div>
                  <h3 className="font-medium flex items-center gap-1">
                    <Calendar className="h-4 w-4" /> Target End Date
                  </h3>
                  <p className="text-muted-foreground">
                    {resourceRequest.targetEndDate ? format(new Date(resourceRequest.targetEndDate), "PPP") : "Ongoing"}
                  </p>
                </div>
                <div>
                  <h3 className="font-medium flex items-center gap-1">
                    <Clock className="h-4 w-4" /> Request Deadline
                  </h3>
                  <p className="text-muted-foreground">
                    {resourceRequest.requestDeadline ? format(new Date(resourceRequest.requestDeadline), "PPP") : "N/A"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-line">{resourceRequest.notes || "No additional notes provided."}</p>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Requirements Tab */}
        <TabsContent value="requirements" className="space-y-4 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Requirements */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Requirements</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="list-disc list-inside space-y-2">
                  {resourceRequest.requirements?.map((req: string, index: number) => (
                    <li key={index}>{req}</li>
                  )) || <li>No specific requirements provided.</li>}
                </ul>
              </CardContent>
            </Card>
            
            {/* Skills */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Skills</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {resourceRequest.skills?.map((skill: string, index: number) => (
                    <Badge key={index} variant="secondary">{skill}</Badge>
                  )) || <p className="text-muted-foreground">No specific skills provided.</p>}
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Tech Stacks */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Tech Stacks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {resourceRequest.techStacks?.map((tech: any, index: number) => (
                  <div key={index} className="border rounded-lg p-4">
                    <h3 className="font-medium text-lg">{tech.name}</h3>
                    <div className="flex justify-between mt-2">
                      <span className="text-muted-foreground">Level:</span>
                      <Badge variant="outline">{tech.level}</Badge>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-muted-foreground">Experience:</span>
                      <span>{tech.yearsOfExperience} years</span>
                    </div>
                  </div>
                )) || <p className="text-muted-foreground">No tech stack requirements provided.</p>}
              </div>
            </CardContent>
          </Card>
          
          {/* Education and Experience */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Education</CardTitle>
              </CardHeader>
              <CardContent>
                <p>{resourceRequest.education || "No specific education requirements."}</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Experience</CardTitle>
              </CardHeader>
              <CardContent>
                <p>{resourceRequest.experience || "No specific experience requirements."}</p>
              </CardContent>
            </Card>
          </div>
          
          {/* Keywords */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xl">Keywords</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {resourceRequest.keywords?.map((keyword: string, index: number) => (
                  <Badge key={index} variant="outline">
                    <Tag className="h-3 w-3 mr-1" />
                    {keyword}
                  </Badge>
                )) || <p className="text-muted-foreground">No keywords provided.</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Working Conditions Tab */}
        <TabsContent value="working-conditions" className="space-y-4 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Working Models */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Working Models</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {resourceRequest.workingModels?.map((model: WorkingModel, index: number) => (
                    <Badge key={index} variant="secondary">
                      {model === WorkingModel.ONSITE && <MapPin className="h-3 w-3 mr-1" />}
                      {model === WorkingModel.REMOTE && <Globe className="h-3 w-3 mr-1" />}
                      {model === WorkingModel.HYBRID && <Briefcase className="h-3 w-3 mr-1" />}
                      {model === WorkingModel.FREELANCER && <Users className="h-3 w-3 mr-1" />}
                      {model}
                    </Badge>
                  )) || <p className="text-muted-foreground">No working models specified.</p>}
                </div>
              </CardContent>
            </Card>
            
            {/* Working Time */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Working Time</CardTitle>
              </CardHeader>
              <CardContent>
                <p>{resourceRequest.workingTime || "No specific working time requirements."}</p>
              </CardContent>
            </Card>
          </div>
          
          {/* Languages */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Language Requirements</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {resourceRequest.languages?.map((language: any, index: number) => (
                  <div key={index} className="border rounded-lg p-4">
                    <h3 className="font-medium text-lg">{language.name}</h3>
                    <div className="flex justify-between mt-2">
                      <span className="text-muted-foreground">Proficiency:</span>
                      <Badge variant="outline">{language.proficiency}</Badge>
                    </div>
                  </div>
                )) || <p className="text-muted-foreground">No language requirements specified.</p>}
              </div>
            </CardContent>
          </Card>
          
          {/* Gender and Code Test */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Gender Preference</CardTitle>
              </CardHeader>
              <CardContent>
                <Badge variant="outline">{resourceRequest.gender}</Badge>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Code Live Test</CardTitle>
              </CardHeader>
              <CardContent>
                <Badge variant={resourceRequest.requiresCodeLiveTest ? "default" : "outline"}>
                  {resourceRequest.requiresCodeLiveTest ? "Required" : "Not Required"}
                </Badge>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Timeline & Compensation Tab */}
        <TabsContent value="timelines" className="space-y-4 pt-4">
          {/* Salary Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Compensation</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium flex items-center gap-1">
                    <Banknote className="h-4 w-4" /> Salary Range
                  </h3>
                  <p className="text-xl font-semibold">
                    {resourceRequest.salary?.min.toLocaleString()} - {resourceRequest.salary?.max.toLocaleString()} {resourceRequest.salary?.currency}/month
                  </p>
                  <Badge className="mt-2">{resourceRequest.salary?.range}</Badge>
                </div>
                <div>
                  <h3 className="font-medium flex items-center gap-1">
                    <Banknote className="h-4 w-4" /> Budget
                  </h3>
                  <p className="text-xl font-semibold">
                    {resourceRequest.budget ? `${resourceRequest.budget.toLocaleString()} USD` : "Not specified"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Timeline Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-muted"></div>
                
                <div className="relative pl-10 pb-6">
                  <div className="absolute left-0 w-6 h-6 rounded-full bg-primary flex items-center justify-center text-primary-foreground">
                    <span className="text-xs">1</span>
                  </div>
                  <div>
                    <h3 className="font-medium">Request Created</h3>
                    <p className="text-muted-foreground">
                      {resourceRequest.requestDate ? format(new Date(resourceRequest.requestDate), "PPP") : "N/A"}
                    </p>
                  </div>
                </div>
                
                {resourceRequest.approvalDate && (
                  <div className="relative pl-10 pb-6">
                    <div className="absolute left-0 w-6 h-6 rounded-full bg-primary flex items-center justify-center text-primary-foreground">
                      <span className="text-xs">2</span>
                    </div>
                    <div>
                      <h3 className="font-medium">Request Approved</h3>
                      <p className="text-muted-foreground">
                        {format(new Date(resourceRequest.approvalDate), "PPP")} by {resourceRequest.approvedBy.name}
                      </p>
                    </div>
                  </div>
                )}
                
                <div className="relative pl-10 pb-6">
                  <div className="absolute left-0 w-6 h-6 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
                    <span className="text-xs">{resourceRequest.approvalDate ? "3" : "2"}</span>
                  </div>
                  <div>
                    <h3 className="font-medium">Deadline for Request</h3>
                    <p className="text-muted-foreground">
                      {resourceRequest.requestDeadline ? format(new Date(resourceRequest.requestDeadline), "PPP") : "No deadline set"}
                    </p>
                  </div>
                </div>
                
                <div className="relative pl-10">
                  <div className="absolute left-0 w-6 h-6 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
                    <span className="text-xs">{resourceRequest.approvalDate ? "4" : "3"}</span>
                  </div>
                  <div>
                    <h3 className="font-medium">Target Start Date</h3>
                    <p className="text-muted-foreground">
                      {resourceRequest.targetStartDate ? format(new Date(resourceRequest.targetStartDate), "PPP") : "No start date set"}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Approval Information */}
          {resourceRequest.approvedBy && (
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Approval Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-medium">Approved By</h3>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-medium">
                        {resourceRequest.approvedBy.name.charAt(0)}
                      </div>
                      <span>{resourceRequest.approvedBy.name}</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium">Approval Date</h3>
                    <p className="mt-2">
                      {resourceRequest.approvalDate ? format(new Date(resourceRequest.approvalDate), "PPP") : "Not approved yet"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Additional Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Additional Information</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-line">
                {resourceRequest.notes || "No additional information provided."}
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
} 